"use client";

import { c as _c } from "react/compiler-runtime";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import { useRef, useState, useEffect, useMemo, useSyncExternalStore } from "react";
import * as THREE from "three";
import { useAnimationConfig } from "@/context/AnimationContext";
import { useNeumorphicTheme } from "@/hooks/useNeumorphicTheme";

// --- PROCEDURAL TEXTURE CACHE ---
const textureCache: Record<string, THREE.CanvasTexture> = {};
const getScratchedTexture = () => {
  if (typeof window === "undefined") return null;
  if (textureCache.scratched) return textureCache.scratched;
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.fillStyle = "#6b7280";
  context.fillRect(0, 0, 1024, 1024);
  context.filter = "blur(15px)";
  for (let i = 0; i < 55; i++) {
    context.fillStyle = `rgba(20, 20, 25, ${Math.random() * 0.5})`;
    context.beginPath();
    context.arc(Math.random() * 1024, Math.random() * 1024, Math.random() * 120 + 30, 0, Math.PI * 2);
    context.fill();
  }
  for (let i = 0; i < 35; i++) {
    context.fillStyle = `rgba(150, 150, 160, ${Math.random() * 0.4})`;
    context.beginPath();
    context.arc(Math.random() * 1024, Math.random() * 1024, Math.random() * 80 + 20, 0, Math.PI * 2);
    context.fill();
  }
  context.filter = "none";
  context.fillStyle = "rgba(0,0,0,0.1)";
  for (let i = 0; i < 7500; i++) {
    context.fillRect(Math.random() * 1024, Math.random() * 1024, Math.random() * 30 + 8, Math.random() * 2 + 1);
  }
  context.fillStyle = "rgba(255,255,255,0.05)";
  for (let i = 0; i < 7500; i++) {
    context.fillRect(Math.random() * 1024, Math.random() * 1024, Math.random() * 30 + 8, Math.random() * 2 + 1);
  }
  for (let i = 0; i < 350; i++) {
    context.beginPath();
    context.fillStyle = `rgba(15, 15, 20, ${Math.random() * 0.6})`;
    const r = Math.random() * 4 + 1;
    context.arc(Math.random() * 1024, Math.random() * 1024, r, 0, Math.PI * 2);
    context.fill();
  }
  for (let i = 0; i < 1500; i++) {
    context.beginPath();
    const isDeep = Math.random() > 0.8;
    context.strokeStyle = isDeep ? "rgba(10,10,10,0.9)" : "rgba(160,160,160,0.5)";
    context.lineWidth = isDeep ? Math.random() * 4 + 1 : Math.random() * 2;
    const x = Math.random() * 1024;
    const y = Math.random() * 1024;
    const len = Math.random() * 80 + 15;
    const angle = Math.random() * Math.PI * 2;
    context.moveTo(x, y);
    let curX = x;
    let curY = y;
    for (let s = 0; s < 4; s++) {
      curX += Math.cos(angle + (Math.random() - 0.5) * 0.4) * (len / 4);
      curY += Math.sin(angle + (Math.random() - 0.5) * 0.4) * (len / 4);
      context.lineTo(curX, curY);
    }
    context.stroke();
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 4;
  textureCache.scratched = tex;
  return tex;
};
const getRopeColorTexture = () => {
  if (typeof window === "undefined") return null;
  if (textureCache.ropeColor) return textureCache.ropeColor;
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.fillStyle = "#888888";
  context.fillRect(0, 0, 512, 512);
  for (let i = -512; i < 1024; i += 128) {
    const startX = i - 512;
    const startY = -512;
    const endX = i + 1024;
    const endY = 1024;
    context.beginPath();
    context.lineWidth = 100;
    context.strokeStyle = "#ffffff";
    context.moveTo(startX, startY);
    context.lineTo(endX, endY);
    context.stroke();
    context.beginPath();
    context.lineWidth = 15;
    context.strokeStyle = "#666666";
    context.moveTo(startX - 45, startY);
    context.lineTo(endX - 45, endY);
    context.stroke();
    context.beginPath();
    context.lineWidth = 15;
    context.strokeStyle = "#666666";
    context.moveTo(startX + 45, startY);
    context.lineTo(endX + 45, endY);
    context.stroke();
    context.lineWidth = 2;
    for (let f = -40; f < 40; f += 6) {
      context.beginPath();
      const jitter = Math.random() * 2 - 1;
      context.strokeStyle = Math.random() > 0.5 ? "#ffffff" : "#aaaaaa";
      context.moveTo(startX + f, startY);
      context.lineTo(endX + f + jitter * 20, endY);
      context.stroke();
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 4;
  tex.repeat.set(2, 40);
  textureCache.ropeColor = tex;
  return tex;
};
const generateRubberTexture = (baseColor: string, cacheKey: string) => {
  if (typeof window === "undefined") return null;
  if (textureCache[cacheKey]) return textureCache[cacheKey];
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.fillStyle = baseColor;
  context.fillRect(0, 0, 512, 512);
  context.filter = "blur(12px)";
  for (let i = 0; i < 20; i++) {
    context.fillStyle = `rgba(10, 10, 10, ${Math.random() * 0.4})`;
    const cx = Math.random() * 512;
    const cy = Math.random() * 512;
    const r = Math.random() * 60 + 15;
    const drawSmudge = (x: number, y: number) => {
      context.beginPath();
      context.arc(x, y, r, 0, Math.PI * 2);
      context.fill();
    };
    drawSmudge(cx, cy);
    if (cx - r < 0) drawSmudge(cx + 512, cy);
    if (cx + r > 512) drawSmudge(cx - 512, cy);
  }
  context.filter = "none";
  for (let i = 0; i < 20000; i++) {
    context.fillStyle = Math.random() > 0.5 ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)";
    context.fillRect(Math.random() * 512, Math.random() * 512, 2, 2);
  }
  context.filter = "blur(6px)";
  context.fillStyle = "rgba(0,0,0,0.6)";
  context.fillRect(0, 0, 512, 24);
  context.fillRect(0, 512 - 24, 512, 24);
  context.filter = "none";
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 4;
  textureCache[cacheKey] = tex;
  return tex;
};
const getKnotColorTexture = () => {
  if (typeof window === "undefined") return null;
  if (textureCache.knotColor) return textureCache.knotColor;
  const ropeColor = getRopeColorTexture();
  if (!ropeColor) return null;
  const tex = ropeColor.clone();
  tex.repeat.set(30, 2);
  tex.needsUpdate = true;
  textureCache.knotColor = tex;
  return tex;
};
const getRopeEndTexture = () => {
  if (typeof window === "undefined") return null;
  if (textureCache.ropeEnd) return textureCache.ropeEnd;
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.fillStyle = "#333333";
  context.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 2500; i++) {
    context.beginPath();
    context.fillStyle = Math.random() > 0.5 ? "#ffffff" : "#aaaaaa";
    const r = Math.random() * 2 + 0.5;
    context.arc(Math.random() * 256, Math.random() * 256, r, 0, Math.PI * 2);
    context.fill();
  }
  const tex = new THREE.CanvasTexture(canvas);
  textureCache.ropeEnd = tex;
  return tex;
};

// Prewarm heavy procedural canvas textures in background idle slice
// so synchronous 2D canvas drawing never blocks the main thread during loader split.
const prewarmProceduralTextures = () => {
  if (typeof window === "undefined") return;
  try {
    getScratchedTexture();
    getRopeColorTexture();
    getKnotColorTexture();
    generateRubberTexture("#eab308", "yellowRubber");
    generateRubberTexture("#4b5563", "grayRubber");
    getRopeEndTexture();
  } catch {
    // Non-fatal prewarm
  }
};
const ROPE_THICKNESS = 0.3;
const Rope = t0 => {
  const $ = _c(47);
  const {
    position,
    barQuaternion,
    pullRotation,
    isYellow: t1,
    knotType: t2,
    removeFirstTail: t3
  } = t0;
  const isYellow = t1 === undefined ? false : t1;
  const knotType = t2 === undefined ? "clove" : t2;
  const removeFirstTail = t3 === undefined ? false : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getRopeColorTexture();
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const ropeColorTexture = t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = getKnotColorTexture();
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const knotColorTexture = t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getRopeEndTexture();
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const ropeEndTexture = t6;
  const r = ROPE_THICKNESS * 1.25;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    const points = [new THREE.Vector3(0, -0.1, r * 1.5), new THREE.Vector3(0, 0.05, r * 1.05), new THREE.Vector3(-r, 0.15, 0), new THREE.Vector3(0, 0.2, -r), new THREE.Vector3(r, 0.2, 0), new THREE.Vector3(0, 0.15, r), new THREE.Vector3(-r * 0.8, 0.05, r * 1.4), new THREE.Vector3(0, -0.05, r), new THREE.Vector3(r, -0.15, 0), new THREE.Vector3(0, -0.2, -r), new THREE.Vector3(-r * 1.5, -0.1, 0)];
    t7 = new THREE.CatmullRomCurve3(points, false, "catmullrom", 0.5);
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const knotCurve = t7;
  const r_0 = ROPE_THICKNESS * 1.25;
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    const points_0 = [new THREE.Vector3(0, 0.05, r_0 * 1.05), new THREE.Vector3(-r_0, 0.15, 0), new THREE.Vector3(0, 0.2, -r_0), new THREE.Vector3(r_0, 0.2, 0), new THREE.Vector3(0, 0.15, r_0), new THREE.Vector3(-r_0 * 0.8, 0.05, r_0 * 1.4), new THREE.Vector3(0, -0.05, r_0), new THREE.Vector3(r_0, -0.15, 0), new THREE.Vector3(0, -0.2, -r_0), new THREE.Vector3(-r_0 * 1.5, -0.1, 0)];
    t8 = new THREE.CatmullRomCurve3(points_0, false, "catmullrom", 0.5);
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const cloveNoTailCurve = t8;
  const r_1 = ROPE_THICKNESS * 1.25;
  let t9;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    const points_1 = [new THREE.Vector3(0, 0, r_1 * 1.5), new THREE.Vector3(0, 0, r_1), new THREE.Vector3(-r_1, 0.1, 0), new THREE.Vector3(0, 0.1, -r_1), new THREE.Vector3(r_1, 0.1, 0), new THREE.Vector3(0, 0, r_1 * 1.2), new THREE.Vector3(-r_1 * 1.5, -0.1, 0)];
    t9 = new THREE.CatmullRomCurve3(points_1, false, "catmullrom", 0.5);
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const simpleWrapCurve = t9;
  const r_2 = ROPE_THICKNESS * 1.25;
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    const points_2 = [new THREE.Vector3(0, -0.2, r_2 * 1.5), new THREE.Vector3(0, -0.25, r_2), new THREE.Vector3(r_2, -0.2, 0), new THREE.Vector3(0, -0.15, -r_2), new THREE.Vector3(-r_2, -0.1, 0), new THREE.Vector3(0, 0, r_2 * 1.1), new THREE.Vector3(r_2, 0.1, 0), new THREE.Vector3(0, 0.2, -r_2), new THREE.Vector3(-r_2, 0.25, 0), new THREE.Vector3(0, 0.15, r_2 * 1.2), new THREE.Vector3(r_2, 0.05, 0), new THREE.Vector3(0, -0.05, -r_2), new THREE.Vector3(-r_2, -0.1, 0), new THREE.Vector3(-r_2 * 1.5, -0.1, 0)];
    t10 = new THREE.CatmullRomCurve3(points_2, false, "catmullrom", 0.5);
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const messyKnotCurve = t10;
  let t11;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <meshStandardMaterial color="#eab308" metalness={0} roughness={0.95} map={ropeColorTexture || undefined} bumpMap={ropeColorTexture || undefined} bumpScale={0.06} />;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const yellowRopeMaterial = t11;
  let t12;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <meshStandardMaterial color="#cccccc" metalness={0} roughness={0.95} map={ropeColorTexture || undefined} bumpMap={ropeColorTexture || undefined} bumpScale={0.06} />;
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  const silverRopeMaterial = t12;
  let t13;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <meshStandardMaterial color="#eab308" metalness={0} roughness={0.95} map={knotColorTexture || undefined} bumpMap={knotColorTexture || undefined} bumpScale={0.06} />;
    $[9] = t13;
  } else {
    t13 = $[9];
  }
  const yellowKnotMaterial = t13;
  let t14;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <meshStandardMaterial color="#cccccc" metalness={0} roughness={0.95} map={knotColorTexture || undefined} bumpMap={knotColorTexture || undefined} bumpScale={0.06} />;
    $[10] = t14;
  } else {
    t14 = $[10];
  }
  const silverKnotMaterial = t14;
  let t15;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <meshStandardMaterial color="#eab308" metalness={0} roughness={0.95} map={ropeEndTexture || undefined} bumpMap={ropeEndTexture || undefined} bumpScale={0.12} side={THREE.DoubleSide} />;
    $[11] = t15;
  } else {
    t15 = $[11];
  }
  const yellowRopeEndMaterial = t15;
  let t16;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <meshStandardMaterial color="#cccccc" metalness={0} roughness={0.95} map={ropeEndTexture || undefined} bumpMap={ropeEndTexture || undefined} bumpScale={0.12} side={THREE.DoubleSide} />;
    $[12] = t16;
  } else {
    t16 = $[12];
  }
  const silverRopeEndMaterial = t16;
  let activeKnotCurve = knotCurve;
  if (knotType === "wrap") {
    activeKnotCurve = simpleWrapCurve;
  }
  if (knotType === "messy") {
    activeKnotCurve = messyKnotCurve;
  }
  if (removeFirstTail && knotType === "clove") {
    activeKnotCurve = cloveNoTailCurve;
  }
  let t17;
  if ($[13] !== activeKnotCurve) {
    t17 = <tubeGeometry args={[activeKnotCurve, 48, 0.08, 12, false]} />;
    $[13] = activeKnotCurve;
    $[14] = t17;
  } else {
    t17 = $[14];
  }
  const t18 = isYellow ? yellowKnotMaterial : silverKnotMaterial;
  let t19;
  if ($[15] !== t17 || $[16] !== t18) {
    t19 = <mesh>{t17}{t18}</mesh>;
    $[15] = t17;
    $[16] = t18;
    $[17] = t19;
  } else {
    t19 = $[17];
  }
  let t20;
  if ($[18] !== activeKnotCurve || $[19] !== isYellow || $[20] !== removeFirstTail) {
    t20 = !removeFirstTail && <mesh position={activeKnotCurve.getPoint(0)} quaternion={new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), activeKnotCurve.getTangent(0).normalize())}><cylinderGeometry args={[0.081, 0.081, 0.05, 16]} />{isYellow ? yellowRopeEndMaterial : silverRopeEndMaterial}</mesh>;
    $[18] = activeKnotCurve;
    $[19] = isYellow;
    $[20] = removeFirstTail;
    $[21] = t20;
  } else {
    t20 = $[21];
  }
  let t21;
  if ($[22] !== activeKnotCurve) {
    t21 = activeKnotCurve.getPoint(1);
    $[22] = activeKnotCurve;
    $[23] = t21;
  } else {
    t21 = $[23];
  }
  let t22;
  if ($[24] !== activeKnotCurve) {
    t22 = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), activeKnotCurve.getTangent(1).normalize());
    $[24] = activeKnotCurve;
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  let t23;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <cylinderGeometry args={[0.081, 0.081, 0.05, 16]} />;
    $[26] = t23;
  } else {
    t23 = $[26];
  }
  const t24 = isYellow ? yellowRopeEndMaterial : silverRopeEndMaterial;
  let t25;
  if ($[27] !== t21 || $[28] !== t22 || $[29] !== t24) {
    t25 = <mesh position={t21} quaternion={t22}>{t23}{t24}</mesh>;
    $[27] = t21;
    $[28] = t22;
    $[29] = t24;
    $[30] = t25;
  } else {
    t25 = $[30];
  }
  let t26;
  if ($[31] !== barQuaternion || $[32] !== t19 || $[33] !== t20 || $[34] !== t25) {
    t26 = <group quaternion={barQuaternion}>{t19}{t20}{t25}</group>;
    $[31] = barQuaternion;
    $[32] = t19;
    $[33] = t20;
    $[34] = t25;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  let t27;
  let t28;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = [0, 10, 0];
    t28 = <cylinderGeometry args={[0.08, 0.08, 20, 12]} />;
    $[36] = t27;
    $[37] = t28;
  } else {
    t27 = $[36];
    t28 = $[37];
  }
  const t29 = isYellow ? yellowRopeMaterial : silverRopeMaterial;
  let t30;
  if ($[38] !== t29) {
    t30 = <mesh position={t27}>{t28}{t29}</mesh>;
    $[38] = t29;
    $[39] = t30;
  } else {
    t30 = $[39];
  }
  let t31;
  if ($[40] !== pullRotation || $[41] !== t30) {
    t31 = <group rotation={pullRotation}>{t30}</group>;
    $[40] = pullRotation;
    $[41] = t30;
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  let t32;
  if ($[43] !== position || $[44] !== t26 || $[45] !== t31) {
    t32 = <group position={position}>{t26}{t31}</group>;
    $[43] = position;
    $[44] = t26;
    $[45] = t31;
    $[46] = t32;
  } else {
    t32 = $[46];
  }
  return t32;
};

// --- THE TACTICAL CARABINER COMPONENT ---
function CarabinerModel() {
  const $ = _c(32);
  const groupRef = useRef(null);
  const {
    size
  } = useThree();
  const responsiveScale = size.width < 768 ? 0.65 : 1;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = state => {
      if (!groupRef.current) {
        return;
      }
      const time = state.clock.elapsedTime;
      const introSpinZ = Math.exp(-time * 1.2) * (Math.PI / 1.5);
      const jitterX = Math.sin(time * 3) * 0.005 + Math.cos(time * 4) * 0.005;
      const jitterY = Math.cos(time * 2.5) * 0.005 + Math.sin(time * 3.5) * 0.005;
      const jitterZ = Math.sin(time * 3.2) * 0.005;
      const pullX = Math.sin(time * 0.8) * 0.04;
      const pullY = Math.cos(time * 0.6) * 0.04;
      const pullZ = Math.sin(time * 0.5) * 0.02;
      groupRef.current.rotation.x = pullX + jitterX;
      groupRef.current.rotation.y = pullY + jitterY;
      groupRef.current.rotation.z = pullZ + jitterZ + introSpinZ;
      const introPullX = Math.exp(-time * 1.2) * Math.cos(time * 3) * -3;
      const introPullY = Math.exp(-time * 1.5) * Math.sin(time * 4) * -2;
      groupRef.current.position.x = pullX * 2 + jitterX * 1.5 + introPullX;
      groupRef.current.position.y = pullY * 2 + jitterY * 1.5 + introPullY;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useFrame(t0);
  const R = 2.5;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new THREE.Vector3(0, 2.5, 0);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new THREE.Vector3(2.5 * Math.cos(-Math.PI / 6), 2.5 * Math.sin(-Math.PI / 6), 0);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      p1: t1,
      p2: t2,
      p3: new THREE.Vector3(-2.5 * Math.cos(-Math.PI / 6), 2.5 * Math.sin(-Math.PI / 6), 0)
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    p1,
    p2,
    p3
  } = t3;
  const getCylinderProps = _temp;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getCylinderProps(p1, p2);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const side1 = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = getCylinderProps(p2, p3);
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const side2 = t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getCylinderProps(p3, p1);
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const side3 = t6;
  const getPt = _temp2;
  let path;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    const p1In = getPt(p1, p3, 0.4);
    const p1Out = getPt(p1, p2, 0.4);
    const p2In = getPt(p2, p1, 0.4);
    const p2Out = getPt(p2, p3, 0.4);
    const p3In = getPt(p3, p2, 0.4);
    const p3Out = getPt(p3, p1, 0.4);
    const bottomMidpoint = p2.clone().lerp(p3, 0.5);
    path = new THREE.CurvePath();
    path.add(new THREE.LineCurve3(bottomMidpoint, p3In));
    path.add(new THREE.QuadraticBezierCurve3(p3In, p3, p3Out));
    path.add(new THREE.LineCurve3(p3Out, p1In));
    path.add(new THREE.QuadraticBezierCurve3(p1In, p1, p1Out));
    path.add(new THREE.LineCurve3(p1Out, p2In));
    path.add(new THREE.QuadraticBezierCurve3(p2In, p2, p2Out));
    path.add(new THREE.LineCurve3(p2Out, bottomMidpoint));
    $[7] = path;
  } else {
    path = $[7];
  }
  const carabinerPath = path;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = getScratchedTexture();
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const scratchedTexture = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = generateRubberTexture("#eab308", "yellowRubber");
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const yellowRubberTexture = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = generateRubberTexture("#4b5563", "grayRubber");
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const grayRubberTexture = t9;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <meshStandardMaterial color="#ffffff" metalness={0.65} roughness={0.3} map={scratchedTexture || undefined} bumpMap={scratchedTexture || undefined} bumpScale={0.03} />;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const metalMaterial = t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <meshStandardMaterial color="#aaaaaa" metalness={0.8} roughness={0.35} map={scratchedTexture || undefined} bumpMap={scratchedTexture || undefined} bumpScale={0.03} />;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const darkMetalMaterial = t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <meshStandardMaterial roughness={0.9} metalness={0.1} map={yellowRubberTexture || undefined} bumpMap={yellowRubberTexture || undefined} bumpScale={0.015} />;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const yellowMaterial = t12;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <meshStandardMaterial roughness={0.9} metalness={0.1} map={grayRubberTexture || undefined} bumpMap={grayRubberTexture || undefined} bumpScale={0.015} />;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const grayMaterial = t13;
  const getPointOnLine = _temp3;
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <mesh><tubeGeometry args={[carabinerPath, 256, 0.3, 32, true]} />{metalMaterial}</mesh>;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <group position={side2.position} quaternion={side2.quaternion}><mesh position={[0, 0, 0]} rotation={[0, Math.PI, 0]}><cylinderGeometry args={[0.39, 0.39, side2.length * 0.4, 32]} />{darkMetalMaterial}</mesh><mesh position={[0, side2.length * 0.25, 0]} rotation={[0, Math.PI, 0]}><cylinderGeometry args={[0.33, 0.33, side2.length * 0.2, 32]} />{grayMaterial}</mesh></group>;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <group position={side3.position} quaternion={side3.quaternion}><mesh position={[0, -0.5, 0]} rotation={[0, Math.PI, 0]}><cylinderGeometry args={[0.315, 0.315, 1.5, 24]} />{yellowMaterial}</mesh></group>;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = getPointOnLine(p1, p2, 0.26);
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Rope position={t17} barQuaternion={side1.quaternion} pullRotation={[0, 0, -Math.PI / 3.5]} isYellow={true} knotType="clove" />;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = getPointOnLine(p1, p2, 0.8);
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  let t20;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Rope position={t19} barQuaternion={side1.quaternion} pullRotation={[0, 0, -Math.PI / 1.7]} knotType="messy" />;
    $[21] = t20;
  } else {
    t20 = $[21];
  }
  let t21;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = getPointOnLine(p2, p3, 0.2);
    $[22] = t21;
  } else {
    t21 = $[22];
  }
  let t22;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Rope position={t21} barQuaternion={side2.quaternion} pullRotation={[0, 0, -Math.PI * 0.85]} isYellow={true} knotType="wrap" />;
    $[23] = t22;
  } else {
    t22 = $[23];
  }
  let t23;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = getPointOnLine(p2, p3, 0.75);
    $[24] = t23;
  } else {
    t23 = $[24];
  }
  let t24;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Rope position={t23} barQuaternion={side2.quaternion} pullRotation={[0, 0, Math.PI * 0.85]} knotType="clove" removeFirstTail={true} />;
    $[25] = t24;
  } else {
    t24 = $[25];
  }
  let t25;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = getPointOnLine(p3, p1, 0.35);
    $[26] = t25;
  } else {
    t25 = $[26];
  }
  let t26;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Rope position={t25} barQuaternion={side3.quaternion} pullRotation={[0, 0, Math.PI / 1.8]} knotType="messy" />;
    $[27] = t26;
  } else {
    t26 = $[27];
  }
  let t27;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = getPointOnLine(p3, p1, 0.8);
    $[28] = t27;
  } else {
    t27 = $[28];
  }
  let t28;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Rope position={t27} barQuaternion={side3.quaternion} pullRotation={[0, 0, Math.PI / 3.5]} isYellow={true} knotType="wrap" />;
    $[29] = t28;
  } else {
    t28 = $[29];
  }
  let t29;
  if ($[30] !== responsiveScale) {
    t29 = <Float speed={2} rotationIntensity={0.2} floatIntensity={0.5}><group ref={groupRef} scale={responsiveScale}>{t14}{t15}{t16}{t18}{t20}{t22}{t24}{t26}{t28}</group></Float>;
    $[30] = responsiveScale;
    $[31] = t29;
  } else {
    t29 = $[31];
  }
  return t29;
}
function _temp3(v1_1, v2_1, t) {
  return v1_1.clone().lerp(v2_1, t);
}
function _temp2(v1_0, v2_0, d) {
  const dir = new THREE.Vector3().subVectors(v2_0, v1_0).normalize();
  return v1_0.clone().add(dir.multiplyScalar(d));
}
function _temp(v1, v2) {
  const distance = v1.distanceTo(v2);
  const position = v1.clone().lerp(v2, 0.5);
  const direction = new THREE.Vector3().subVectors(v2, v1).normalize();
  const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
  return {
    position,
    quaternion,
    length: distance
  };
}
function WebGLCleanup() {
  const $ = _c(4);
  const {
    gl
  } = useThree();
  let t0;
  if ($[0] !== gl?.domElement) {
    t0 = () => {
      const handleContextLost = _temp4;
      const domElement = gl?.domElement;
      if (domElement) {
        domElement.addEventListener("webglcontextlost", handleContextLost, false);
      }
      return () => {
        if (domElement) {
          domElement.removeEventListener("webglcontextlost", handleContextLost);
        }
      };
    };
    $[0] = gl?.domElement;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== gl) {
    t1 = [gl];
    $[2] = gl;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
}

// --- MAIN WRAPPER COMPONENT ---
function _temp4(event) {
  event.preventDefault();
}
export default function HeroCarabiner3D() {
  const $ = _c(16);
  const mounted = useSyncExternalStore(_temp6, _temp7, _temp8);
  const [shouldRenderWebGL, setShouldRenderWebGL] = useState(false);
  const {
    isAnimationsDisabled
  } = useAnimationConfig();
  const isNeumorphic = useNeumorphicTheme();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (typeof window !== "undefined") {
        const scheduleIdle = (window as unknown as {
          requestIdleCallback?: (cb: () => void, opts?: {
            timeout: number;
          }) => void;
        }).requestIdleCallback || _temp9;
        scheduleIdle(prewarmProceduralTextures, {
          timeout: 1000
        });
      }
      const html = document.documentElement;
      if (html.classList.contains("loader-complete") || html.classList.contains("no-animations")) {
        const immediate = setTimeout(() => setShouldRenderWebGL(true), 0);
        return () => clearTimeout(immediate);
      }
      const warmupTimer = setTimeout(() => {
        setShouldRenderWebGL(true);
      }, 350);
      return () => {
        clearTimeout(warmupTimer);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  if (!mounted) {
    return null;
  }
  const t2 = `absolute inset-0 transition-opacity duration-300 ${isNeumorphic ? "opacity-[0.28]" : "opacity-[0.12]"}`;
  const t3 = `repeating-radial-gradient(circle at 50% 50%, transparent 0, transparent 40px, ${isNeumorphic ? "#475569" : "#ffffff"} 40px, ${isNeumorphic ? "#475569" : "#ffffff"} 41.5px)`;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      backgroundImage: t3,
      backgroundPosition: "center center"
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t4) {
    t5 = <div className={t2} style={t4} />;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = `absolute inset-0 transition-opacity duration-1000 ease-in-out ${shouldRenderWebGL && !isAnimationsDisabled ? "opacity-100" : "opacity-0"}`;
  let t7;
  if ($[7] !== isAnimationsDisabled || $[8] !== shouldRenderWebGL) {
    t7 = shouldRenderWebGL && !isAnimationsDisabled && <Canvas camera={{
      position: [0, 0, 10],
      fov: 45
    }} gl={{
      antialias: true
    }} dpr={[1, 1.25]}><WebGLCleanup /><ambientLight intensity={0.85} /><directionalLight position={[10, 10, 8]} intensity={2.2} /><directionalLight position={[0, 4, 10]} intensity={1.6} color="#ffffff" /><directionalLight position={[-10, 8, -5]} intensity={1.2} color="#e2e8f0" /><spotLight position={[0, 0, 10]} intensity={0.6} penumbra={1} /><CarabinerModel /></Canvas>;
    $[7] = isAnimationsDisabled;
    $[8] = shouldRenderWebGL;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t5 || $[14] !== t8) {
    t9 = <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">{t5}{t8}</div>;
    $[13] = t5;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function _temp9(cb) {
  return setTimeout(cb, 100);
}
function _temp8() {
  return false;
}
function _temp7() {
  return true;
}
function _temp6() {
  return _temp5;
}
function _temp5() {}